// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import DeleteProjectDialog from 'components/project/DeleteDialog';
import ResetModelDialog from 'components/project/ResetModelDialog';
import Options from 'components/projectSelection/Options';
import PageContent from 'components/treactui-template/organisms/page/PageContent';
import PageHeader from 'components/treactui-template/organisms/page/PageHeader';
import PageWrapper from 'components/treactui-template/organisms/page/PageWrapper';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { DefaultBreakpoints } from 'hooks/useMediaQuery';
import FilterGridContainer, { ActionButtonShell, PageTitleShell } from 'layout/header/FilterGridContainer';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { Navigate, useNavigate, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { AppRoutes, ProjectSubPath } from 'utilities/router/AppRoutes';
import ShareProjectDialog from 'components/project/ShareProjectDialog';
import { useAppDispatch } from 'store/hooks';
import CreateProjectDialog from 'components/projectSelection/CreateProjectDialog';
import ParamsWithId from 'model/ParamsWithId';
import SSimProvider from 'components/project/label/similarity/SSimProvider';
import ImagesProvider from 'components/project/images/ImagesProvider';
import { clearEvaluationProgressCache } from 'store/reducers/projectEvaluationSlice';
import QCProjectDataProvider from 'components/common/QCProjectDataProvider';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import { DataFilterProvider } from 'components/project/data/DataFilterProvider';
import { Breadcrumb, Breadcrumbs, SubMenuHorizontal } from '@tls/treact-ui';

export default function Project() {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams<ParamsWithId>();
  const { loading, selectedProject } = useProjectsMetadata(id);
  const { projectName, lockedBy } = selectedProject ?? {};

  const showAlgorithmsTab = useMemo(
    () => selectedProject?.projectUseCase?.useCase === UseCase.QUALITY_CHECK,
    [selectedProject?.projectUseCase?.useCase]
  );

  const showDataTab = useMemo(
    () =>
      selectedProject?.projectUseCase?.useCase === UseCase.QUALITY_CHECK ||
      (selectedProject?.projectUseCase?.modelClasses?.length ?? 0) > 2,
    [selectedProject?.projectUseCase?.useCase, selectedProject?.projectUseCase?.modelClasses]
  );

  const navigate = useNavigate();
  const goToSubpath = useCallback((path: string) => () => navigate(path), [navigate]);

  const headerReference = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(clearEvaluationProgressCache());
  }, [dispatch]);

  if (
    [
      AppRoutes.Project,
      `${AppRoutes.Project}/`,
      `${AppRoutes.Project}/${id}`,
      AppRoutes.ProjectWithId(id ?? '', ''),
    ].includes(location.pathname)
  ) {
    return <Navigate to={AppRoutes.ProjectWithId(id ?? '', ProjectSubPath.Label)} />;
  }

  if (
    !loading &&
    selectedProject &&
    location.pathname?.startsWith(AppRoutes.ProjectAlgorithmsFullPath(id ?? '')) &&
    !showAlgorithmsTab
  ) {
    return <Navigate to={AppRoutes.ProjectLabelFullPath(id ?? '')} />;
  }

  return (
    <QCProjectDataProvider>
      <PageWrapper>
        <PageHeader>
          <FilterGridContainer $pxBreakpoint={DefaultBreakpoints.md} $showActionButton $showView $showFilter={false}>
            <PageTitleShell>
              <Breadcrumbs data-testid='project-breadcrumbs'>
                <Breadcrumb
                  label={t('projects.title')}
                  onClick={goToSubpath(AppRoutes.Projects)}
                  data-testid='project-breadcrumb-projects-button'
                />
                <Breadcrumb label={projectName ?? ''} selected data-testid='project-breadcrumb-current' />
              </Breadcrumbs>
            </PageTitleShell>
            <ActionButtonShell>
              <Options projectId={id ?? ''} projectName={projectName} lockedBy={lockedBy} />
            </ActionButtonShell>
          </FilterGridContainer>
        </PageHeader>
        <PageContent displayAsPaper disableScroll>
          <div ref={headerReference} data-testid='project-submenu'>
            <SubMenuHorizontal data-testid='project-submenu-tabs'>
              {[
                <SubMenuHorizontal.SubMenuTab
                  label={t('project.label.title')}
                  selected={location.pathname?.startsWith(AppRoutes.ProjectLabelFullPath(id ?? ''))}
                  onClick={goToSubpath(AppRoutes.ProjectLabelFullPath(id ?? ''))}
                  centered
                  key='label'
                  data-testid='project-submenu-label-tab'
                />,
                showAlgorithmsTab && (
                  <SubMenuHorizontal.SubMenuTab
                    label={t('project.algorithms.title')}
                    selected={location.pathname?.startsWith(AppRoutes.ProjectAlgorithmsFullPath(id ?? ''))}
                    onClick={goToSubpath(AppRoutes.ProjectAlgorithmsFullPath(id ?? ''))}
                    centered
                    key='algorithms'
                    data-testid='project-submenu-algorithms-tab'
                  />
                ),
                showDataTab && (
                  <SubMenuHorizontal.SubMenuTab
                    label={t('project.data.title')}
                    selected={location.pathname?.startsWith(AppRoutes.ProjectDataFullPath(id ?? ''))}
                    onClick={goToSubpath(AppRoutes.ProjectDataFullPath(id ?? ''))}
                    centered
                    key='data'
                    data-testid='project-submenu-data-tab'
                  />
                ),
                <SubMenuHorizontal.SubMenuTab
                  label={t('project.evaluation.title')}
                  selected={location.pathname?.startsWith(AppRoutes.ProjectEvaluationFullPath(id ?? ''))}
                  onClick={goToSubpath(AppRoutes.ProjectEvaluationFullPath(id ?? ''))}
                  centered
                  key='evaluation'
                  data-testid='project-submenu-evaluation-tab'
                />,
              ].filter(
                (tab): tab is React.ReactElement<React.ComponentProps<typeof SubMenuHorizontal.SubMenuTab>> =>
                  tab !== false
              )}
            </SubMenuHorizontal>
          </div>
          <PageContainer $headerHeight={headerReference.current?.clientHeight} data-testid='project-content'>
            <ImagesProvider>
              <SSimProvider>
                <DataFilterProvider projectId={id}>
                  <Outlet />
                </DataFilterProvider>
              </SSimProvider>
            </ImagesProvider>
          </PageContainer>
        </PageContent>
        <CreateProjectDialog />
        <DeleteProjectDialog />
        <ResetModelDialog />
        <ShareProjectDialog />
      </PageWrapper>
    </QCProjectDataProvider>
  );
}

const PageContainer = styled.div<{ $headerHeight?: number }>`
  height: calc(100% - ${({ $headerHeight }) => $headerHeight}px);
  position: relative;
`;
